import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';

export interface DefaultSettings {
  defaultPrioritize: 'free' | 'speed' | 'balance' | 'value' | 'intelligence';
  defaultAIs: number;
}

// Hardcoded fallbacks if Firestore values are not set
const FALLBACK_DEFAULTS: DefaultSettings = {
  defaultPrioritize: 'intelligence',
  defaultAIs: 3,
};

export class DefaultSettingsService {
  private static settingsCache: DefaultSettings | null = null;
  private static lastFetch: number = 0;
  private static CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  /**
   * Get the default settings for prioritize and AIs
   * Fetches from system/config document
   * @returns The default settings object
   */
  static async getDefaults(): Promise<DefaultSettings> {
    try {
      // Return cached settings if still valid
      const now = Date.now();
      if (this.settingsCache && (now - this.lastFetch) < this.CACHE_DURATION) {
        return this.settingsCache;
      }

      console.log('🔧 Fetching default settings from Firestore...');

      const configRef = doc(db, 'system', 'config');
      const configDoc = await getDoc(configRef);

      if (!configDoc.exists()) {
        console.warn('⚠️ No system/config found, using hardcoded defaults');
        return FALLBACK_DEFAULTS;
      }

      const data = configDoc.data();
      
      // Extract default settings with fallbacks
      const settings: DefaultSettings = {
        defaultPrioritize: this.validatePrioritize(data?.defaultPrioritize) || FALLBACK_DEFAULTS.defaultPrioritize,
        defaultAIs: this.validateAIs(data?.defaultAIs) ?? FALLBACK_DEFAULTS.defaultAIs,
      };

      // Cache the settings
      this.settingsCache = settings;
      this.lastFetch = now;

      console.log('✅ Default settings loaded:', settings);

      return settings;
    } catch (error) {
      console.error('❌ Error fetching default settings:', error);
      return FALLBACK_DEFAULTS;
    }
  }

  /**
   * Validate prioritize value
   */
  private static validatePrioritize(value: unknown): 'free' | 'speed' | 'balance' | 'value' | 'intelligence' | null {
    const validValues = ['free', 'speed', 'balance', 'value', 'intelligence'];
    if (typeof value === 'string' && validValues.includes(value)) {
      return value as 'free' | 'speed' | 'balance' | 'value' | 'intelligence';
    }
    return null;
  }

  /**
   * Validate AIs value (must be 1-10)
   */
  private static validateAIs(value: unknown): number | null {
    if (typeof value === 'number' && value >= 1 && value <= 10) {
      return Math.floor(value);
    }
    return null;
  }

  /**
   * Clear the cached settings (useful for testing or forcing refresh)
   */
  static clearCache(): void {
    this.settingsCache = null;
    this.lastFetch = 0;
    console.log('🗑️ Default settings cache cleared');
  }

  /**
   * Get fallback defaults (synchronous, for initial render)
   */
  static getFallbackDefaults(): DefaultSettings {
    return { ...FALLBACK_DEFAULTS };
  }
}



